/**
 * PickFlowPage - Phase 2D-2
 *
 * Pick Mode prediction flow.
 * Now uses real Markets/Outcomes from Pick slate API.
 */
import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { usePool } from '../hooks/usePool';
import { usePickSlate } from '../hooks/usePickSlate';
import { usePoolMembership } from '../hooks';
import { PickGMService } from '../api';
import { LoadingSpinner, Alert, Button } from '../components/common';
import { PickForm } from '../components/picks/PickForm';
import type { PickSlateEvent, SubmitPickRequest } from '../types';

export function PickFlowPage(): JSX.Element {
  const { poolId } = useParams<{ poolId: string }>();
  const navigate = useNavigate();
  const safePoolId = poolId || '';

  const { pool, loading: poolLoading, error: poolError } = usePool(safePoolId);
  const { membership, loading: membershipLoading } = usePoolMembership(safePoolId);
  const { events, loading: slateLoading, error: slateError } = usePickSlate(safePoolId);

  const [selectedEvent, setSelectedEvent] = useState<PickSlateEvent | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const handleSubmit = async (request: SubmitPickRequest) => {
    if (!poolId) return;

    setSubmitting(true);
    setSubmitError(null);

    try {
      await PickGMService.submitPick(poolId, request);
      navigate(`/pools/${poolId}`);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to submit prediction');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    setSelectedEvent(null);
  };

  // Loading state
  const loading = poolLoading || membershipLoading || slateLoading;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // Error / validation
  if (poolError || !pool) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <Alert variant="error">{poolError || 'Pool not found'}</Alert>
        <Button variant="secondary" className="mt-4" onClick={() => navigate('/pools')}>
          Back to Pools
        </Button>
      </div>
    );
  }

  if (pool.mode !== 'pick') {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <Alert variant="warning">This pool uses GM mode, not Pick mode.</Alert>
        <Button variant="secondary" className="mt-4" onClick={() => navigate(`/pools/${poolId}/gm`)}>
          Go to GM Mode
        </Button>
      </div>
    );
  }

  if (!membership) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <Alert variant="warning">You must join this pool before making predictions.</Alert>
        <Button variant="secondary" className="mt-4" onClick={() => navigate(`/pools/${poolId}`)}>
          Go to Pool Details
        </Button>
      </div>
    );
  }

  if (slateError) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <Alert variant="error">{slateError}</Alert>
        <Button variant="secondary" className="mt-4" onClick={() => navigate(`/pools/${poolId}`)}>
          Back to Pool
        </Button>
      </div>
    );
  }

  // Filter to only show unlocked events with markets
  const availableEvents = events.filter(e => !e.is_locked && e.markets.length > 0);

  if (availableEvents.length === 0) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <Alert variant="warning">No events available for predictions at this time.</Alert>
        <Button variant="secondary" className="mt-4" onClick={() => navigate(`/pools/${poolId}`)}>
          Back to Pool
        </Button>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-100">{pool.pool_name}</h1>
        <p className="text-slate-400">Pick Mode - Make a Prediction</p>
      </div>

      {submitError && (
        <Alert variant="error" className="mb-4">{submitError}</Alert>
      )}

      {/* Event Selection */}
      {!selectedEvent ? (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-slate-200">Select an Event</h2>
          <div className="grid gap-3">
            {availableEvents.map(event => (
              <button
                key={event.event_id}
                onClick={() => setSelectedEvent(event)}
                className="p-4 bg-slate-800 border border-slate-600 rounded-lg text-left hover:border-slate-400 transition-colors"
              >
                <div className="font-semibold text-slate-100">{event.event_name}</div>
                <div className="text-sm text-slate-400">
                  {event.home_team} vs {event.away_team}
                </div>
                <div className="text-xs text-slate-500 mt-1">
                  {event.markets.length} markets available
                </div>
              </button>
            ))}
          </div>
        </div>
      ) : (
        <PickForm
          event={selectedEvent}
          membership={membership}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          loading={submitting}
          error={submitError}
        />
      )}
    </div>
  );
}
